/*!
 * Copyright 2020 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from "@angular/router";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {Select} from "@ngxs/store";
import {combineLatest, Observable} from "rxjs";
import {filter, first, map} from "rxjs/operators";
import {TpSections} from "../models/tp-sections-info";
import {TpService} from "../services/tp.service";

/**
 * Load the treatment plan sections configuration depending on the context associated to the
 * current patient.
 *
 * Depending on the context some sections could available or not.
 * If no context is assigned, all the sections will be available.
 */
@Injectable({
  providedIn: "root",
})
export class TpConfigResolver implements Resolve<TpSections | null> {
  @Select(CommonState.patientContext) patientContext$: Observable<
    RegistryContexts | null | undefined
  >;
  @Select(CommonState.isHealthWorker) isHealthWorker$: Observable<boolean | undefined>;

  constructor(private tpService: TpService) {}

  resolve(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<TpSections | null> {
    return combineLatest([this.patientContext$, this.isHealthWorker$]).pipe(
      filter((ctx, isHW) => ctx !== undefined && isHW !== undefined),
      first(), // Wait for context and take first
      map(([ctx, isHW]) => {
        // Change viewd sections depending on the current context
        let mConf: TpSections;

        // Hide tp configurations depending on the context
        mConf = this.tpService.getTpSections();
        if (
          !ctx?.includes(RegistryContexts.STOMACHCARE) &&
          !ctx?.includes(RegistryContexts.WOUNDCARE) &&
          !ctx?.includes(RegistryContexts.PERIOPERATIVE)
        ) {
          mConf = this.tpService.getTpSections(["base.operations"]);
        }

        if (isHW) {
          //console.log('HW: ', mConf)
          //delete mConf.advanced.items.anamnesi;
          delete mConf.base.items.exams;
        }

        return mConf;
      }),
    );
  }
}
